import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product title.
 * This component listens for variant update events and updates the title display accordingly.
 * It handles title updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductTitle extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateTitle);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateTitle);
  }

  /**
   * Updates the product title and actions.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateTitle = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const doc = event.detail.data.html;
    const productId = this.dataset.productId;
    /** @type {Element | null} */
    let newProductTitle = null;
    if (productId) {
      newProductTitle = doc.querySelector(`product-title[data-product-id="${productId}"]`);
    }
    if (!newProductTitle) {
      newProductTitle = doc.querySelector('product-title');
    }
    if (!newProductTitle) return;

    if (newProductTitle instanceof HTMLElement && newProductTitle.dataset.title) {
      this.dataset.title = newProductTitle.dataset.title;
    }

    const newTitleText = getProductTitleText(newProductTitle);
    if (!newTitleText) return;

    const currentTitleText = getProductTitleText(this);
    if (newTitleText !== currentTitleText) {
      setProductTitleText(this, newTitleText);
    }
  };
}

/**
 * Reads title text from lean markup or legacy row/text-block wrappers (section rendering).
 * @param {Element} productTitle
 * @returns {string}
 */
function getProductTitleText(productTitle) {
  const row = productTitle.querySelector('.product-title-row');
  if (row) {
    const content = row.querySelector('.product-title-text, .text-block') ?? row;
    const paragraph = content.querySelector('p');
    return (paragraph?.textContent ?? content.textContent ?? '').trim();
  }

  return (productTitle.textContent ?? '').trim();
}

/**
 * Updates title text in place without replacing theme-editor wrapper markup when present.
 * @param {ProductTitle} productTitle
 * @param {string} titleText
 */
function setProductTitleText(productTitle, titleText) {
  const heading = productTitle.querySelector('.product-title-heading');
  if (heading) {
    heading.textContent = titleText;
    return;
  }

  const row = productTitle.querySelector('.product-title-row');
  if (row) {
    const content = row.querySelector('.product-title-text, .text-block') ?? row;
    const paragraph = content.querySelector('p');
    if (paragraph) {
      paragraph.textContent = titleText;
    } else {
      content.textContent = titleText;
    }
    return;
  }

  productTitle.textContent = titleText;
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}
